import gt from '@naverpay/hidash/gt'
import {
    add,
    addDays,
    differenceInCalendarDays,
    differenceInDays,
    getDay,
    getYear,
    isBefore,
    isEqual,
    isSameDay,
    isToday,
    isTomorrow,
    isValid,
    isYesterday,
    max,
    min,
    parseISO,
    startOfDay,
    sub,
    subDays,
    format as dateFnsFormat,
    addMinutes,
    parse,
    Duration,
} from 'date-fns'

import {TimeFormat} from './time'
import {formatDateFns} from './utils'

export const isPossibleDateString = (date: string): boolean => Date.parse(date) > -1

/** 입력된 시간이 현재시간보다 큰지 확인합니다 */
export const gtDateNow = (date: Date | number | string): boolean => {
    if (typeof date === 'string' && !isPossibleDateString(date)) {
        return false
    }
    return gt(new Date(date).getTime(), Date.now())
}

/**
 *
 * @param date 입력 날짜
 * @param duration 구할 날짜 까지의 기간
 * @returns 입력된 날짜 기준 duration 만큼 다음 날짜의 시작값을 반환한다.
 */
export const getNextStartDay = (date: Date | number | string, duration: Duration): Date | null => {
    if (typeof date === 'string' && !isPossibleDateString(date)) {
        return null
    }
    return startOfDay(add(new Date(date), duration))
}

export const addDay = (date: Date | number | string, duration: Duration): Date | null => {
    if (typeof date === 'string' && !isPossibleDateString(date)) {
        return null
    }
    return add(new Date(date), duration)
}

type DateDuration = {
    year?: number
    month?: number
    week?: number
    day?: number
    hour?: number
    minute?: number
    second?: number
}

/** 주어진 Date 에서 원하는 만큼 가산후 반환 */
export const addDate = (date: Date | number, duration: DateDuration): Date => {
    return add(date, {
        years: duration.year,
        months: duration.month,
        weeks: duration.week,
        days: duration.day,
        hours: duration.hour,
        minutes: duration.minute,
        seconds: duration.second,
    })
}

/** 주어진 Date 에서 원하는 만큼 차감후 반환 */
export const subDate = (date: Date | number, duration: DateDuration): Date => {
    return sub(date, {
        years: duration.year,
        months: duration.month,
        weeks: duration.week,
        days: duration.day,
        hours: duration.hour,
        minutes: duration.minute,
        seconds: duration.second,
    })
}

/** 주어진 Date 에서 원하는 만큼 가산 및 차감후 반환 */
export const rangeDate = (
    date: Date | number,
    options: {
        start: DateDuration
        end: DateDuration
    },
) => {
    return {
        start: subDate(date, options.start),
        end: addDate(date, options.end),
    }
}

export const isEqualDay = (leftDate: Date | number | string, rightDate: Date | number | string): boolean => {
    const left = new Date(leftDate)
    const right = new Date(rightDate)

    if (!isValid(left) || !isValid(right)) {
        return false
    }

    return isEqual(left, right)
}

/**
 * fromDay ~ targetDay 까지 디데이를 반환합니다.
 * fromDay는 옵셔널 필드로, 주입하지 않으면 오늘 (today)로 계산합니다.
 *
 * @example
 * How many calendar days are between
 * 2024 June 2 23:59:00 and 3 2024 June 3 00:01:00?
    const result = differenceInCalendarDays(
        new Date(2011, 6, 3, 0, 1),
        new Date(2011, 6, 2, 23, 59)
    )
 * => 1
 *
 */

export const getDday = ({
    targetDay,
    fromDay,
}: {
    targetDay: Date | number | string
    fromDay?: Date | string | number
}) => {
    const fromDate = fromDay ? new Date(fromDay).getTime() : new Date().getTime()
    const targetDate = new Date(targetDay).getTime()

    return differenceInCalendarDays(targetDate, fromDate)
}

/**
 * fromDay ~ targetDay 까지의 full day count를 반환합니다.
 * > 24시간당 하루로 취급
 * > 위의 getDday 함수는 달력상으로 날짜가 다르면 day count를 증가시킴
 * fromDay는 옵셔널 필드로, 주입하지 않으면 오늘 (today)로 계산합니다.
 *
 * @example
 * How many full days are between
 * 2024 June 2 23:59:00 and 3 2024 June 3 00:01:00?
    const result = differenceInCalendarDays(
        new Date(2011, 6, 3, 0, 1),
        new Date(2011, 6, 2, 23, 59)
    )
 * => 0
 *
 */
export const getFullDays = ({
    targetDay,
    fromDay,
}: {
    targetDay: Date | number | string
    fromDay?: Date | number | string
}) => {
    const targetDate = new Date(targetDay)
    const fromDate = fromDay ? new Date(fromDay) : new Date()

    return differenceInDays(targetDate, fromDate)
}

/**
 * @param date 20240408
 * @param format -
 * @return 2024-04-08
 */
export function getDateString({date, format}: {date?: string; format?: string; size?: number}): string {
    if (date && date.length > 7) {
        return [date.substring(0, 4), date.substring(4, 6), date.substring(6)].join(format ?? '-')
    }
    return ''
}

export const isBeforeDate = ({date, dateToCompare}: {date: Date | number | string; dateToCompare: Date | number}) => {
    if (typeof date === 'string' && !isPossibleDateString(date)) {
        return false
    }
    return isBefore(new Date(date), dateToCompare)
}

type DateStatusType = 'DAY_BEFORE_YESTERDAY' | 'YESTERDAY' | 'TODAY' | 'TOMORROW' | 'DAY_AFTER_TOMORROW' | 'UNKNOWN'
type DateWeekType = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY' | 'UNKNOWN'
const DateWeekMap: Record<number, DateWeekType> = {
    0: 'SUNDAY',
    1: 'MONDAY',
    2: 'TUESDAY',
    3: 'WEDNESDAY',
    4: 'THURSDAY',
    5: 'FRIDAY',
    6: 'SATURDAY',
}

/** 날짜 정보 */
export const getDateInfo = (
    date: Date | number | string,
): {
    value: Maybe<Date>
    status: DateStatusType
    weekDay: DateWeekType
} => {
    if (typeof date === 'string' && !isPossibleDateString(date)) {
        return {
            value: null,
            status: 'UNKNOWN',
            weekDay: 'UNKNOWN',
        }
    }
    const dateValue = new Date(date)
    return {
        value: dateValue,
        status: (function () {
            if (isSameDay(dateValue, subDays(Date.now(), 2))) {
                return 'DAY_BEFORE_YESTERDAY'
            }
            if (isYesterday(dateValue)) {
                return 'YESTERDAY'
            }
            if (isToday(dateValue)) {
                return 'TODAY'
            }
            if (isTomorrow(dateValue)) {
                return 'TOMORROW'
            }
            if (isSameDay(dateValue, addDays(Date.now(), 2))) {
                return 'DAY_AFTER_TOMORROW'
            }
            return 'UNKNOWN'
        })(),
        weekDay: DateWeekMap[getDay(dateValue)] ?? 'UNKNOWN',
    }
}

export const formatDate = (source: Date | number | string, formatStr: string) => {
    const sourceDate = new Date(source)

    return dateFnsFormat(sourceDate, formatStr)
}

export const splitDateAndTime = (dateTimeString: string) => {
    try {
        const dateStr = formatDate(dateTimeString, 'yyyy-MM-dd')
        const timeStr = formatDate(dateTimeString, 'HH:mm')

        return {
            date: dateStr,
            time: timeStr,
        }
    } catch {
        return {
            date: '',
            time: '',
        }
    }
}

/** HH:mm 형식으로 포맷된 시간을 반환 */
export const getFormattedTime = (date: Date | number | string, option?: {minutesToAdd?: number}) => {
    return formatDateFns(addMinutes(new Date(date), option?.minutesToAdd ?? 0), TimeFormat['HH:mm'])
}

export const getDateValues = (values: Date[]) => {
    return {
        min: min([...values]),
        max: max([...values]),
    }
}

/**
 * 입력된 날짜가 지난년도 인지 확인
 * @param {string} date ISO 형식 날짜 문자열
 */
export const isPrevYear = (date: string): boolean => {
    const currentYear = getYear(new Date())
    return getYear(parseISO(date)) < currentYear
}

export const formatISO = (
    date: Date | string | number,
    options: {
        inputFormat?: string
    } = {},
): string => {
    try {
        const result: Date = (function () {
            if (typeof date === 'string' && options.inputFormat) {
                return new Date(parse(date, options.inputFormat, new Date()))
            }
            return new Date(date)
        })()
        return result.toISOString()
    } catch {
        return ''
    }
}

export const formatISOByGuard = (date?: Date | string | number) => {
    if (date == null) {
        return date
    }
    return formatISO(date)
}
